import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FAQList, postContactUs } from "../services/website.services";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../utils/Constants";
import * as Yup from "yup";
import { useFormik } from "formik";
import "./swayyd.css";
import logo from "../../../assets/website/images/logo.png";
import closetMobileScreen from "../../../assets/website/images/closet-mobile-screen.png";
import heroImg from "../../../assets/website/images/Hero.png";
import closetImg from "../../../assets/website/images/closet.png";
import plannerImg from "../../../assets/website/images/planner.png";
import communityImg from "../../../assets/website/images/community.png";

function SwayydPage() {
  const globalDetails = useSelector((state) => state.globalDetails?.data);
  const [faqs, setFaqs] = useState([]);
  const [loadingContact, setLoadingContact] = useState(false);

  const contactSchema = Yup.object().shape({
    name: Yup.string().min(2, "Name is too short").required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    subject: Yup.string().required("Subject is required"),
    message: Yup.string().min(10, "Message is too short").required("Message is required"),
  });

  const formik = useFormik({
    initialValues: { name: "", email: "", subject: "", message: "" },
    validationSchema: contactSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoadingContact(true);
      try {
        const res = await postContactUs({ ...values, type: "web" });
        if (res) {
          Swal.fire({ icon: "success", text: res.message || "Message sent!", ...SWAL_SETTINGS });
          resetForm();
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingContact(false);
      }
    },
  });

  useEffect(() => {
    // Fetch FAQs
    FAQList("website").then((res) => {
      if (res.success && res.data?.list) {
        setFaqs(res.data.list.slice(0, 5));
      }
    });
  }, []);
  console.log("globalDetails",globalDetails);

  const appStoreLink = globalDetails?.app_store_url || "";
  const playStoreLink = globalDetails?.play_store_url || "https://play.google.com/store/apps/details?id=com.veysh";

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="hero-backdrop" aria-hidden="true">
          <span className="hero-backdrop__orb hero-backdrop__orb--grape" />
          <span className="hero-backdrop__orb hero-backdrop__orb--rose" />
          <span className="hero-backdrop__glass hero-backdrop__glass--left" />
          <span className="hero-backdrop__glass hero-backdrop__glass--right" />
        </div>

        <div className="swayyd-container hero__inner">
          <div className="hero__grid">
            <div className="hero-content">
              <div className="hero-eyebrow">Your Wardrobe, Reimagined</div>
              <h1 className="hero-headline">
                Your closet. <em>Finally</em>
                <br />
                working for you.
              </h1>
              <p className="hero-sub">
                {globalDetails?.title || "Veysh"} is the wardrobe intelligence app that helps you know what you own,
                build outfits for every occasion, and let go of what no longer serves you —
                without the overwhelm.
              </p>
              <div className="hero-actions">
                <a href="#contact" className="btn-primary">
                  <span className="btn-label">Contact us</span>
                  <span className="btn-icon btn-icon-arrow" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </a>
                <a href="#features" className="btn-secondary">
                  <span className="btn-play-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="8 5 18 12 8 19" />
                    </svg>
                  </span>
                  <span className="btn-label">See how it works</span>
                </a>
              </div>
              <div className="hero-trust">
                <div className="hero-trust-avatars" aria-hidden="true">
                  <span className="hero-trust-avatar hero-trust-avatar-1" />
                  <span className="hero-trust-avatar hero-trust-avatar-2" />
                  <span className="hero-trust-avatar hero-trust-avatar-3" />
                  <span className="hero-trust-avatar hero-trust-avatar-4" />
                </div>
                <span className="hero-trust-text">
                  Be among the first to try <strong>{globalDetails?.title || "Veysh"}</strong>.
                </span>
              </div>
            </div>

            <div className="hero-visual-stage">
              <div className="hero-visual-card">
                <div className="hero-visual-glow" aria-hidden="true" />

                <div className="hero-visual">
                  <div className="float-card float-card-tag">
                    <div className="fc-row">
                      <div className="fc-dot" />
                      <div className="fc-label">142 items catalogued</div>
                    </div>
                  </div>

                  <div className="float-card float-card-1">
                    <div className="fc-label">Cost per wear</div>
                    <div className="fc-val">₹43</div>
                    <div className="fc-sub">↓ 62% this month</div>
                  </div>

                  <div className="phone-shell">
                    <div className="phone-notch" aria-hidden="true" />
                    <div className="phone-screen">
                      <img src={closetMobileScreen} alt="Closet Mobile Screen" className="ps-img" />
                    </div>
                  </div>

                  <div className="float-card float-card-2">
                    <div className="fc-row">
                      <div className="fc-dot" />
                      <div className="fc-label">Bae! is thinking…</div>
                    </div>
                    <div className="fc-val fc-val-sm">Perfect for Diwali 🪔</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="swayyd-section problem" id="problem">
        <div className="problem-inner">
          <span className="section-eyebrow">The Problem</span>
          <h2 className="section-title">
            A wardrobe full of clothes,<br />
            <em style={{ color: "#c084fc" }}>nothing feels right</em>
          </h2>
          <p className="section-sub">
            Most people wear only a fraction of what they own, buy things they never touch,
            and lose time every morning stuck in front of a wardrobe that has no system.
          </p>

          <div className="problem-stats">
            <div className="problem-stat">
              <div className="ps-big">56<span>%</span></div>
              <div className="ps-desc">of the average wardrobe<br />goes unworn regularly</div>
              <div className="ps-source">M&S / Oxfam Shwopping Study, 2016</div>
            </div>
            <div className="problem-stat">
              <div className="ps-big">40<span>%</span></div>
              <div className="ps-desc">of clothing bought<br />is never worn at all</div>
              <div className="ps-source">Wardrobe Statistics Report, 2026</div>
            </div>
            <div className="problem-stat">
              <div className="ps-big">15<span>min</span></div>
              <div className="ps-desc">lost every morning<br />deciding what to wear</div>
              <div className="ps-source">M&S / Oxfam Shwopping Study, 2016</div>
            </div>
          </div>

          <div className="problem-cards">
            <div className="problem-card">
              <span className="pc-icon">🤯</span>
              <div className="pc-title">"I have nothing to wear"</div>
              <div className="pc-text">Despite owning dozens of pieces, most people open their wardrobe and feel stuck — because there's no system, and half of what they own is invisible to them.</div>
            </div>
            <div className="problem-card">
              <span className="pc-icon">📅</span>
              <div className="pc-title">Events catch you off guard</div>
              <div className="pc-text">A wedding next Saturday, an office dinner on Tuesday, Diwali next month — outfit stress piles up fast without a planner working alongside your wardrobe.</div>
            </div>
            <div className="problem-card">
              <span className="pc-icon">🏷️</span>
              <div className="pc-title">No idea what anything costs</div>
              <div className="pc-text">That kurta worn twice costs ₹750 per wear. The daily jeans? ₹15. Knowing the real cost of each piece changes how you shop and what you reach for first.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="swayyd-section features" id="features">
        <span className="section-eyebrow">Core Features</span>
        <h2 className="section-title">Everything your wardrobe<br /><em>deserves</em></h2>

        {/* 01 Digital Closet */}
        <div className="features-grid">
          <div className="feature-visual" style={{ background: "linear-gradient(135deg,var(--lilac),#fce4ec)" }}>
            <img src={closetImg} alt="Your digital closet" className="fv-phone-img" />
            <div className="fv-badge fv-b1">
              <div className="fvb-label">Items catalogued</div>
              <div className="fvb-val"><span>62</span> pieces</div>
            </div>
            <div className="fv-badge fv-b2">
              <div className="fvb-label">Most worn</div>
              <div className="fvb-val">Denim Jeans · <span>110×</span></div>
            </div>
          </div>
          <div className="feature-content">
            <div className="feat-num">01</div>
            <h3 className="feat-title">Your digital closet — organised at last</h3>
            <p className="feat-desc">
              Add everything you own — clothes, shoes, bags, accessories — and see it all
              in one place. Browse by category, colour, brand, or occasion.
              No more forgetting what you have.
            </p>
            <div className="feat-points">
              <div className="feat-point"><div className="fp-dot"></div><span>Add items by photo — Veysh reads the details and fills them in automatically</span></div>
              <div className="feat-point"><div className="fp-dot"></div><span>Track cost-per-wear for every item — see your wardrobe's real return on investment</span></div>
              <div className="feat-point"><div className="fp-dot"></div><span>Filter by colour, occasion, brand, season, or price at a glance</span></div>
              <div className="feat-point"><div className="fp-dot"></div><span>Surface items you haven't worn in months — the ones worth rediscovering or letting go</span></div>
            </div>
          </div>
        </div>

        <div className="feat-divider"></div>

        {/* 02 Outfit Builder + Planner */}
        <div className="features-grid reverse">
          <div className="feature-visual" style={{ background: "linear-gradient(135deg,#e8f5e9,var(--lilac))" }}>
            <img src={plannerImg} alt="Build outfits" className="fv-phone-img" />
            <div className="fv-badge fv-b1">
              <div className="fvb-label">Next event</div>
              <div className="fvb-val">Wedding · <span>Sat</span></div>
            </div>
            <div className="fv-badge fv-b2">
              <div className="fvb-label">Outfits planned</div>
              <div className="fvb-val"><span>4</span> this week</div>
            </div>
          </div>
          <div className="feature-content">
            <div className="feat-num">02</div>
            <h3 className="feat-title">Build outfits. Plan ahead. No last-minute panic.</h3>
            <p className="feat-desc">
              Mix and match from your own wardrobe using the drag-and-drop outfit builder.
              Pin each look to a date on the planner and walk into every event
              without scrambling at the last minute.
            </p>
            <div className="feat-points">
              <div className="feat-point"><div className="fp-dot"></div><span>Drag items onto a canvas to build and preview complete looks</span></div>
              <div className="feat-point"><div className="fp-dot"></div><span>Tag outfits by occasion — work, wedding, casual, festive, travel</span></div>
              <div className="feat-point"><div className="fp-dot"></div><span>Calendar view of upcoming events with outfit assignments</span></div>
              <div className="feat-point"><div className="fp-dot"></div><span>Save looks to your personal Lookbook to revisit and repeat</span></div>
            </div>
          </div>
        </div>

        <div className="feat-divider"></div>

        {/* 03 Community */}
        <div className="features-grid">
          <div className="feature-visual" style={{ background: "linear-gradient(135deg,#fff3e0,#fce4ec)" }}>
            <img src={communityImg} alt="A community that gets your style" className="fv-phone-img" />
            <div className="fv-badge fv-b1">
              <div className="fvb-label">Community</div>
              <div className="fvb-val">Growing <span>daily</span></div>
            </div>
            <div className="fv-badge fv-b2">
              <div className="fvb-label">Outfit shared</div>
              <div className="fvb-val"><span>142</span> likes ❤️</div>
            </div>
          </div>
          <div className="feature-content">
            <div className="feat-num">03</div>
            <h3 className="feat-title">A community that gets your style</h3>
            <p className="feat-desc">
              Share your outfits, discover how others style similar pieces,
              and find fresh ideas without the noise of a public social feed.
              Real wardrobes. Real inspiration.
            </p>
            <div className="feat-points">
              <div className="feat-point"><div className="fp-dot"></div><span>Post your OOTD and tag the actual items from your own wardrobe</span></div>
              <div className="feat-point"><div className="fp-dot"></div><span>Discover new ways to style pieces you already own</span></div>
              <div className="feat-point"><div className="fp-dot"></div><span>Save community outfits to your lookbook for inspiration</span></div>
              <div className="feat-point"><div className="fp-dot"></div><span>Fashion-positive, inclusive space — not a highlight reel</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* BAE! AI */}
      <section className="swayyd-section ai-section" id="bae">
        <div className="ai-inner">
          <span className="section-eyebrow">AI Stylist</span>
          <h2 className="ai-headline">
            Meet <em>Bae!</em> — your<br />personal style intelligence
          </h2>
          <p className="ai-sub">
            Bae! learns your wardrobe, your taste, and your life.
            Ask anything. Get styled in seconds — from clothes you already own.
          </p>

          <div className="ai-features">
            <div className="ai-feat">
              <div className="aif-icon">✨</div>
              <div className="aif-title">Daily Outfit Suggestions</div>
              <div className="aif-desc">
                Bae! considers your upcoming events and how often you've worn each piece —
                then recommends the right outfit each morning from your actual wardrobe.
                No indecision. No shopping required.
              </div>
            </div>
            <div className="ai-feat">
              <div className="aif-icon">💬</div>
              <div className="aif-title">Style Advisor Chat</div>
              <div className="aif-desc">
                Ask Bae! anything: "What do I wear to a rooftop wedding?" or
                "What am I not wearing enough of?" Honest, personalised answers
                based on your actual wardrobe — not generic advice from the internet.
              </div>
            </div>
          </div>

          <div className="ai-chat">
            <div className="ai-chat-label">Bae! in action</div>
            <div className="chat-msgs">
              <div className="chat-msg user">
                <div className="chat-av u">🙂</div>
                <div className="chat-bubble">I have a Diwali dinner on Saturday. What should I wear?</div>
              </div>
              <div className="chat-msg">
                <div className="chat-av">B!</div>
                <div className="chat-bubble">Your embroidered kurta hasn't been worn in 4 months — it's perfect for this. Pair it with the ivory palazzo and your gold kolhapuris. That's a complete festive look, nothing to buy.</div>
              </div>
              <div className="chat-msg user">
                <div className="chat-av u">🙂</div>
                <div className="chat-bubble">I keep wearing the same 5 outfits. What am I ignoring?</div>
              </div>
              <div className="chat-msg">
                <div className="chat-av">B!</div>
                <div className="chat-bubble">You have 12 pieces tagged "casual" that haven't been worn in over 60 days. Your floral skirt and the olive linen shirt have never been paired — want me to build a look around them?</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="swayyd-section faq" id="faq">
        <div className="problem-inner">
          <span className="section-eyebrow">Questions</span>
          <h2 className="section-title">Common <em>Queries</em></h2>
          <div className="problem-cards" style={{ gridTemplateColumns: "1fr", textAlign: "left", gap: "12px", marginTop: "40px" }}>
            {faqs.length > 0 ? faqs.map((faq, i) => (
              <details key={i} className="problem-card" style={{ cursor: "pointer" }}>
                <summary style={{ fontWeight: 600, fontSize: "16px", listStyle: "none", display: "flex", justifyContent: "space-between" }}>
                  {faq.title}
                  <span>↓</span>
                </summary>
                <div style={{ marginTop: "12px", color: "rgba(15, 15, 15, 0.45)", fontSize: "14px", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: faq.description }} />
              </details>
            )) : (
              <p className="ps-desc">Loading FAQs...</p>
            )}
          </div>
        </div>
      </section>

      {/* CONTACT/CTA SECTION */}
      <section className="cta-section" id="contact">
        <div className="cta-inner" style={{ maxWidth: "800px" }}>
          <h2 className="cta-title">Ready for a <em>smarter</em> closet?</h2>
          <p className="cta-sub">
            Join the early access list or send us a message. Our team is here to help you transform your wardrobe.
          </p>

          <form onSubmit={formik.handleSubmit} style={{ textAlign: "left", background: "rgba(255,255,255,0.05)", padding: "40px", borderRadius: "24px", marginBottom: "60px", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
              <div>
                <input
                  name="name"
                  placeholder="Your Name"
                  className="hero-sub"
                  style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "12px", color: "white", marginBottom: "0" }}
                  onChange={formik.handleChange}
                  value={formik.values.name}
                />
                {formik.errors.name && <div style={{ color: "var(--rose)", fontSize: "12px", marginTop: "4px" }}>{formik.errors.name}</div>}
              </div>
              <div>
                <input
                  name="email"
                  placeholder="Your Email"
                  className="hero-sub"
                  style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "12px", color: "white", marginBottom: "0" }}
                  onChange={formik.handleChange}
                  value={formik.values.email}
                />
                {formik.errors.email && <div style={{ color: "var(--rose)", fontSize: "12px", marginTop: "4px" }}>{formik.errors.email}</div>}
              </div>
            </div>
            <div style={{ marginBottom: "20px" }}>
              <input
                name="subject"
                placeholder="Subject"
                className="hero-sub"
                style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "12px", color: "white", marginBottom: "0" }}
                onChange={formik.handleChange}
                value={formik.values.subject}
              />
              {formik.errors.subject && <div style={{ color: "var(--rose)", fontSize: "12px", marginTop: "4px" }}>{formik.errors.subject}</div>}
            </div>
            <div style={{ marginBottom: "20px" }}>
              <textarea
                name="message"
                placeholder="Your Message"
                className="hero-sub"
                rows="4"
                style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "12px", color: "white", marginBottom: "0", resize: "none" }}
                onChange={formik.handleChange}
                value={formik.values.message}
              />
              {formik.errors.message && <div style={{ color: "var(--rose)", fontSize: "12px", marginTop: "4px" }}>{formik.errors.message}</div>}
            </div>
            <button type="submit" disabled={loadingContact} className="btn-primary" style={{ width: "100%", justifyContent: "center", cursor: "pointer" }}>
              {loadingContact ? "Sending..." : "Submit Message"}
            </button>
          </form>

          <div className="cta-badges">
            <a href={appStoreLink} target="_blank" rel="noopener noreferrer" className="swayyd-store-badge">
              <div className="sb-icon"></div>
              <div className="sb-text">
                <span className="sb-sup">Download on the</span>
                <span className="sb-name">App Store</span>
              </div>
            </a>
            <a href={playStoreLink} target="_blank" rel="noopener noreferrer" className="swayyd-store-badge">
              <div className="sb-icon">▶</div>
              <div className="sb-text">
                <span className="sb-sup">Get it on</span>
                <span className="sb-name">Google Play</span>
              </div>
            </a>
          </div>
          <p className="cta-disclaimer">
            Veysh is currently in development. Early access sign-ups will be notified on launch.<br />
            Bae! is an AI assistant. Styling suggestions are for personal use and do not constitute professional advice.
          </p>
        </div>
      </section>
    </>
  );
}

export default SwayydPage;
